import get from 'lodash/get';
import { useContext } from 'react';
import { CoreContext } from '../context/index.js';
import logger from './logger.js';
import { P as PRODUCT } from '../paths-ZKN3m62_.js';
import { B as BackorderStatus, S as StockStatus, a as ProductType } from '../product-_pRduMz7.js';
import { useRouter } from 'next/router';
import './logToSlack.js';

const getProductUrlKeyFromPath = (path) => 
// Extract product "urlKey" from url
// E.g.: /bedding/legs/ITEM001 -> ['bedding', 'legs', 'ITEM001'] -> ITEM001
path[path.length - 1];
const isBackorderAllowed = (backorderStatus) => [BackorderStatus.Allow, BackorderStatus.AllowAndNotify].includes(backorderStatus);
const isProductInStock = (stockStatus) => stockStatus === StockStatus.InStock;
const isProductAvailableForSale = (backorderStatus, stockStatus) => {
    const isBackorderAllowedValue = isBackorderAllowed(backorderStatus);
    const isProductInStockValue = isProductInStock(stockStatus);
    return isBackorderAllowedValue || isProductInStockValue;
};
const filterFeaturedProducts = (items, cartItems) => {
    let filteredItems = [];
    filteredItems = items
        .filter(item => isProductAvailableForSale(item.backorder_status, item.stock_status))
        .map(item => {
        if (item.variants) {
            const firstVariant = item.variants.find(variant => isProductAvailableForSale(variant.product.backorder_status, variant.product.stock_status)).product;
            return {
                ...item,
                id: firstVariant.id,
                parent_sku: item.sku,
                sku: firstVariant.sku,
                backorder_status: firstVariant.backorder_status,
                stock_status: firstVariant.stock_status,
                price_range: firstVariant.price_range,
                thumbnail: firstVariant.thumbnail,
            };
        }
        return item;
    });
    if (cartItems) {
        filteredItems = filteredItems.filter(item => !cartItems.some(itemCart => itemCart.product.sku === item.sku));
    }
    return filteredItems;
};
const getStockInfo = (backorderStatus, stockStatus, quantityValue, t) => {
    const quantity = Number.isInteger(quantityValue) ? quantityValue : 0;
    const stockInfo = {
        availableForSale: true,
        message: '',
        qty: quantity,
    };
    if (!backorderStatus || !stockStatus) {
        stockInfo.availableForSale = false;
        stockInfo.message = t('$common.$validation.productNotAvailable');
        return stockInfo;
    }
    const isBackorderAllowedValue = isBackorderAllowed(backorderStatus);
    const isProductInStockValue = isProductInStock(stockStatus) && (quantity > 0 || isBackorderAllowedValue);
    if (isBackorderAllowedValue)
        return stockInfo;
    if (!isProductInStockValue) {
        stockInfo.availableForSale = false;
        stockInfo.message = t('$common.$labels.outStock');
        return stockInfo;
    }
    if (quantity < 9) {
        stockInfo.message = t('$common.$validation.qtyLeft', { number: quantity });
        return stockInfo;
    }
    return stockInfo;
};
const getAddonName = (name) => name.split('-')[0].trim();
const comparePriceValues = (oldPrice, newPrice, label) => oldPrice !== newPrice ? `${label}: ${oldPrice} -> ${newPrice}` : '';
const comparePriceRangeAttribute = (oldPriceRange, newPriceRange) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const errList = [];
    const oldMinPrice = oldPriceRange.minimum_price;
    const oldMaxPrice = oldPriceRange.maximum_price;
    const newMinPrice = newPriceRange.minimum_price;
    const newMaxPrice = newPriceRange.maximum_price;
    const minRegularPriceDiff = comparePriceValues((_a = oldMinPrice === null || oldMinPrice === void 0 ? void 0 : oldMinPrice.regular_price) === null || _a === void 0 ? void 0 : _a.value, (_b = newMinPrice === null || newMinPrice === void 0 ? void 0 : newMinPrice.regular_price) === null || _b === void 0 ? void 0 : _b.value, 'min_price.regular_price');
    if (minRegularPriceDiff) {
        errList.push(minRegularPriceDiff);
    }
    const minFinalPriceDiff = comparePriceValues((_c = oldMinPrice === null || oldMinPrice === void 0 ? void 0 : oldMinPrice.final_price) === null || _c === void 0 ? void 0 : _c.value, (_d = newMinPrice === null || newMinPrice === void 0 ? void 0 : newMinPrice.final_price) === null || _d === void 0 ? void 0 : _d.value, 'min_price.final_price');
    if (minFinalPriceDiff) {
        errList.push(minFinalPriceDiff);
    }
    const maxRegularPriceDiff = comparePriceValues((_e = oldMaxPrice === null || oldMaxPrice === void 0 ? void 0 : oldMaxPrice.regular_price) === null || _e === void 0 ? void 0 : _e.value, (_f = newMaxPrice === null || newMaxPrice === void 0 ? void 0 : newMaxPrice.regular_price) === null || _f === void 0 ? void 0 : _f.value, 'max_price.regular_price');
    if (maxRegularPriceDiff) {
        errList.push(maxRegularPriceDiff);
    }
    const maxFinalPriceDiff = comparePriceValues((_g = oldMaxPrice === null || oldMaxPrice === void 0 ? void 0 : oldMaxPrice.final_price) === null || _g === void 0 ? void 0 : _g.value, (_h = newMaxPrice === null || newMaxPrice === void 0 ? void 0 : newMaxPrice.final_price) === null || _h === void 0 ? void 0 : _h.value, 'max_price.final_price');
    if (maxFinalPriceDiff) {
        errList.push(maxFinalPriceDiff);
    }
    return errList;
};
/* eslint-disable @typescript-eslint/no-explicit-any */
const compareOtherAttribute = (oldValue, newValue, label) => {
    if (label === 'quantity') {
        // Only log quantity changes if either value is 0, unless both are 0
        if (oldValue === 0 && newValue === 0) {
            return '';
        }
        if (oldValue === 0 || newValue === 0) {
            return `${label}: ${oldValue} -> ${newValue}`;
        }
        return '';
    }
    return oldValue !== newValue ? `${label}: ${oldValue} -> ${newValue}` : '';
};
// Send an alert to error tracking system
// if some data (e.g. price, stock_status) are outdated
// so that to be aware that a server-side cache needs to be cleared
const checkProductOutdatedData = (oldData, newData, productName, productSku) => {
    const attributesList = ['backorder_status', 'stock_status', 'quantity', 'price_range'];
    const diff = [];
    attributesList.forEach(attribute => {
        if (attribute === 'price_range') {
            const errList = comparePriceRangeAttribute(oldData.price_range, newData.price_range);
            diff.push(...errList);
        }
        else {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const errMsg = compareOtherAttribute(oldData[attribute], newData[attribute], attribute);
            if (errMsg) {
                diff.push(errMsg);
            }
        }
    });
    // Send message to error tracking system if an outdated cache found
    if (diff.length) {
        let message = `Outdated cache data found for product "${productName}" (${productSku}):\n`;
        message += diff.join(',\n');
        logger(message, { category: 'api', type: 'error' });
    }
};
const updateRelatedProductsWithDynamicData = (relatedProducts, relatedProductsDynamic) => relatedProducts.map((currentItem, index) => {
    var _a;
    const { sku: currentSku } = currentItem;
    let dynamicItem;
    if (((_a = relatedProductsDynamic[index]) === null || _a === void 0 ? void 0 : _a.sku) === currentSku) {
        // RelatedProductDynamic item position matches a RelatedProduct position
        // just use it
        dynamicItem = relatedProductsDynamic[index];
    }
    else {
        // RelatedProductDynamic item position does not matches a RelatedProduct position
        // try to find it
        dynamicItem = relatedProductsDynamic.find(item => item.sku === currentSku);
    }
    if (!dynamicItem)
        return currentItem;
    const { backorder_status: dynamicBackorderStatus, stock_status: dynamicStockStatus, quantity: dynamicQuantity, price_range: dynamicPriceRange, } = dynamicItem;
    const oldData = {
        backorder_status: currentItem.backorder_status,
        stock_status: currentItem.stock_status,
        quantity: currentItem.quantity,
        price_range: currentItem.price_range,
    };
    const newData = {
        backorder_status: dynamicBackorderStatus,
        stock_status: dynamicStockStatus,
        quantity: dynamicQuantity,
        price_range: dynamicPriceRange,
    };
    checkProductOutdatedData(oldData, newData, currentItem.name, currentItem.sku);
    return {
        ...currentItem,
        backorder_status: dynamicBackorderStatus,
        stock_status: dynamicStockStatus,
        quantity: dynamicQuantity,
        price_range: dynamicPriceRange,
    };
});
const getProductWithMergedDynamicData = (product, productDynamic) => {
    if ((product === null || product === void 0 ? void 0 : product.sku) !== (productDynamic === null || productDynamic === void 0 ? void 0 : productDynamic.sku))
        return product;
    // SimpleProduct
    if (product.type_id === ProductType.Simple && productDynamic.type_id === ProductType.Simple) {
        const { backorder_status: dynamicBackorderStatus, stock_status: dynamicStockStatus, quantity: dynamicQuantity, price_range: dynamicPriceRange, related_products: dynamicRelatedProducts, eco_fee: dynamicEcoFee, } = productDynamic;
        const oldData = {
            backorder_status: product.backorder_status,
            stock_status: product.stock_status,
            quantity: product.quantity,
            price_range: product.price_range,
        };
        const newData = {
            backorder_status: dynamicBackorderStatus,
            stock_status: dynamicStockStatus,
            quantity: dynamicQuantity,
            price_range: dynamicPriceRange,
            eco_fee: dynamicEcoFee,
        };
        checkProductOutdatedData(oldData, newData, product.name, product.sku);
        return {
            ...product,
            backorder_status: dynamicBackorderStatus,
            stock_status: dynamicStockStatus,
            quantity: dynamicQuantity,
            price_range: dynamicPriceRange,
            eco_fee: dynamicEcoFee,
            related_products: updateRelatedProductsWithDynamicData(product.related_products, dynamicRelatedProducts),
        };
    }
    // ConfigurableProduct
    if (product.type_id === ProductType.Configurable &&
        productDynamic.type_id === ProductType.Configurable) {
        const dynamicVariants = productDynamic.variants;
        // Update ConfigurableProduct variants
        const updatedVariants = product.variants.map((currentVariant, index) => {
            var _a, _b;
            const { sku: currentSku, related_products: currentRelatedProducts } = currentVariant.product;
            let dynamicVariant;
            if (((_b = (_a = dynamicVariants[index]) === null || _a === void 0 ? void 0 : _a.product) === null || _b === void 0 ? void 0 : _b.sku) === currentSku) {
                // DynamicVariant item position matches a ProductVariant position
                // just use it
                dynamicVariant = dynamicVariants[index];
            }
            else {
                // DynamicVariant item position does not matches a ProductVariant position
                // try to find it
                dynamicVariant = dynamicVariants.find(item => item.product.sku === currentSku);
            }
            if (!dynamicVariant)
                return currentVariant;
            const { backorder_status: dynamicBackorderStatus, stock_status: dynamicStockStatus, quantity: dynamicQuantity, price_range: dynamicPriceRange, eco_fee: dynamicEcoFee, related_products: dynamicRelatedProducts, } = dynamicVariant.product;
            const oldData = {
                backorder_status: currentVariant.product.backorder_status,
                stock_status: currentVariant.product.stock_status,
                quantity: currentVariant.product.quantity,
                price_range: currentVariant.product.price_range,
            };
            const newData = {
                backorder_status: dynamicBackorderStatus,
                stock_status: dynamicStockStatus,
                quantity: dynamicQuantity,
                price_range: dynamicPriceRange,
                eco_fee: dynamicEcoFee,
            };
            checkProductOutdatedData(oldData, newData, currentVariant.product.name, currentVariant.product.sku);
            return {
                ...currentVariant,
                product: {
                    ...currentVariant.product,
                    backorder_status: dynamicBackorderStatus,
                    stock_status: dynamicStockStatus,
                    quantity: dynamicQuantity,
                    price_range: dynamicPriceRange,
                    eco_fee: dynamicEcoFee,
                    related_products: updateRelatedProductsWithDynamicData(currentRelatedProducts, dynamicRelatedProducts),
                },
            };
        });
        const { backorder_status: dynamicBackorderStatus, stock_status: dynamicStockStatus, quantity: dynamicQuantity, price_range: dynamicPriceRange, eco_fee: dynamicEcoFee, related_products: dynamicRelatedProducts, } = productDynamic;
        const oldData = {
            backorder_status: product.backorder_status,
            stock_status: product.stock_status,
            quantity: product.quantity,
            price_range: product.price_range,
        };
        const newData = {
            backorder_status: dynamicBackorderStatus,
            stock_status: dynamicStockStatus,
            quantity: dynamicQuantity,
            price_range: dynamicPriceRange,
            eco_fee: dynamicEcoFee,
        };
        checkProductOutdatedData(oldData, newData, product.name, product.sku);
        return {
            ...product,
            backorder_status: dynamicBackorderStatus,
            stock_status: dynamicStockStatus,
            quantity: dynamicQuantity,
            price_range: dynamicPriceRange,
            eco_fee: dynamicEcoFee,
            related_products: updateRelatedProductsWithDynamicData(product.related_products, dynamicRelatedProducts),
            variants: updatedVariants,
        };
    }
    // By default return a current product
    return product;
};
const getProductLink = (urlKey, assignedCategories = [], locale) => {
    const categoriesPath = String(get(assignedCategories, [assignedCategories.length - 1, 'url_path'], '')).replace(/^\//, '');
    let path = String(urlKey).replace(/^\//, ''); // remove first "/" symbol if exists
    if (categoriesPath) {
        path = `${categoriesPath}/${path}`;
    }
    path = PRODUCT.replace('[...path]', path);
    return locale ? `/${locale}${path}` : path;
};
// Used for the product multi-language url
const getRouteProductLink = (path, locale) => {
    const url = PRODUCT.replace('[...path]', path);
    return `/${locale}${url}`;
};
const getProductLinkFromUrlRewrites = (urlRewrites, locale) => {
    if (!Array.isArray(urlRewrites))
        return '';
    const categoryUrlItem = urlRewrites.find(urlRewrite => {
        if (!Array.isArray(urlRewrite.parameters))
            return false;
        const categoryParameterIndex = urlRewrite.parameters.findIndex(parameter => (parameter === null || parameter === void 0 ? void 0 : parameter.name) === 'category');
        return categoryParameterIndex !== -1;
    });
    const url = categoryUrlItem === null || categoryUrlItem === void 0 ? void 0 : categoryUrlItem.url;
    if (!url)
        return '';
    const path = PRODUCT.replace('[...path]', url);
    return locale ? `/${locale}${path}` : path;
};
const getConfigurableOptionsText = (configurableOptions) => {
    if (!Array.isArray(configurableOptions))
        return '';
    return configurableOptions.map(option => option.value_label).join(', ');
};
const getProductStructuredData = (product, locale) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
    if (!product)
        return {};
    const { data: { siteConfig: { siteName }, }, } = useContext(CoreContext);
    const { asPath } = useRouter();
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL;
    const brand = siteName;
    const url = `${siteUrl}/${locale}${asPath}`.split('?')[0];
    const images = [product.image.url, ...product.additional_image.map(image => image.url)];
    const priceRange = product.price_range;
    const oldMinPrice = get(priceRange, ['minimum_price', 'regular_price', 'value']);
    const oldMaxPrice = get(priceRange, ['maximum_price', 'regular_price', 'value']);
    const newMinPrice = get(priceRange, ['minimum_price', 'final_price', 'value']);
    const newMaxPrice = get(priceRange, ['maximum_price', 'final_price', 'value']);
    // Props
    const mainItemName = product.name;
    const category = product.categories.map(productCategory => productCategory.name).join(' > ');
    const description = product.meta_description;
    const keywords = product.meta_keyword;
    const priceCurrency = get(product, 'price_range.minimum_price.final_price.currency');
    const price = get(product, 'price_range.minimum_price.final_price.value', 0);
    const minPrice = newMinPrice || oldMinPrice;
    const maxPrice = oldMaxPrice || newMaxPrice;
    const availability = product.stock_status === StockStatus.InStock
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock';
    const variants = [];
    const colorSet = new Set();
    const materialSet = new Set();
    const sizeSet = new Set();
    const variesBy = [];
    if (product.variants && product.variants.length > 0) {
        product.variants.forEach(variant => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
            const variantSku = variant.product.sku;
            const variantPrice = get(variant.product, 'price_range.minimum_price.final_price.value', 0);
            const variantPriceRange = variant.product.price_range;
            const variantOldMinPrice = get(variantPriceRange, [
                'minimum_price',
                'regular_price',
                'value',
            ]);
            const variantOldMaxPrice = get(variantPriceRange, [
                'maximum_price',
                'regular_price',
                'value',
            ]);
            const variantNewMinPrice = get(variantPriceRange, ['minimum_price', 'final_price', 'value']);
            const variantNewMaxPrice = get(variantPriceRange, ['maximum_price', 'final_price', 'value']);
            const variantMinPrice = variantNewMinPrice || variantOldMinPrice;
            const variantMaxPrice = variantOldMaxPrice || variantNewMaxPrice;
            const variantAvailability = variant.product.stock_status === StockStatus.InStock
                ? 'https://schema.org/InStock'
                : 'https://schema.org/OutOfStock';
            let variantName = mainItemName;
            const extensionNames = [];
            let variantURL = url.replace('http://', 'https://');
            variantURL = variantURL.endsWith('/') ? variantURL.slice(0, -1) : variantURL;
            if (variant.attributes) {
                variantURL += '?';
            }
            variant.attributes.forEach(attribute => {
                variantURL += `${attribute.code}=${attribute.label}&`;
            });
            variantURL = variantURL.endsWith('&') ? variantURL.slice(0, -1) : variantURL;
            let variantSize = '';
            let variantColor = '';
            let variantMaterial = '';
            if ((_a = variant.product.structured_data) === null || _a === void 0 ? void 0 : _a.size) {
                variantSize = (_b = variant.product.structured_data) === null || _b === void 0 ? void 0 : _b.size;
                sizeSet.add(variantSize);
            }
            if ((_c = variant.product.structured_data) === null || _c === void 0 ? void 0 : _c.color) {
                variantColor = (_d = variant.product.structured_data) === null || _d === void 0 ? void 0 : _d.color;
                colorSet.add(variantColor);
            }
            if ((_e = variant.product.structured_data) === null || _e === void 0 ? void 0 : _e.material) {
                variantMaterial = (_f = variant.product.structured_data) === null || _f === void 0 ? void 0 : _f.material;
                materialSet.add(variantMaterial);
            }
            if (extensionNames.length > 0) {
                variantName = `${variantName} (${extensionNames.join(', ')})`;
            }
            const variantAdditionalProperties = [];
            const variantAdditionalPropertiesPairs = [
                [
                    (_g = variant.product.structured_data) === null || _g === void 0 ? void 0 : _g.additional_property_1_name,
                    (_h = variant.product.structured_data) === null || _h === void 0 ? void 0 : _h.additional_property_1_value,
                ],
                [
                    (_j = variant.product.structured_data) === null || _j === void 0 ? void 0 : _j.additional_property_2_name,
                    (_k = variant.product.structured_data) === null || _k === void 0 ? void 0 : _k.additional_property_2_value,
                ],
                [
                    (_l = variant.product.structured_data) === null || _l === void 0 ? void 0 : _l.additional_property_3_name,
                    (_m = variant.product.structured_data) === null || _m === void 0 ? void 0 : _m.additional_property_3_value,
                ],
                [
                    (_o = variant.product.structured_data) === null || _o === void 0 ? void 0 : _o.additional_property_4_name,
                    (_p = variant.product.structured_data) === null || _p === void 0 ? void 0 : _p.additional_property_4_value,
                ],
                [
                    (_q = variant.product.structured_data) === null || _q === void 0 ? void 0 : _q.additional_property_5_name,
                    (_r = variant.product.structured_data) === null || _r === void 0 ? void 0 : _r.additional_property_5_value,
                ],
            ];
            variantAdditionalPropertiesPairs.forEach(additionalPropertyPair => {
                if (additionalPropertyPair[0] && additionalPropertyPair[1]) {
                    variantAdditionalProperties.push({
                        '@type': 'PropertyValue',
                        name: additionalPropertyPair[0],
                        value: additionalPropertyPair[1],
                    });
                }
            });
            variants.push({
                '@context': 'https://schema.org',
                '@type': 'Product',
                sku: variantSku,
                image: images,
                name: variantName,
                url: variantURL,
                category,
                description,
                keywords,
                size: variantSize || undefined,
                color: variantColor || undefined,
                material: variantMaterial || undefined,
                additionalProperty: variantAdditionalProperties.length > 0 ? variantAdditionalProperties : undefined,
                brand: {
                    '@type': 'Brand',
                    name: brand,
                },
                audience: {
                    '@type': 'PeopleAudience',
                    suggestedGender: 'unisex',
                },
                offers: {
                    '@type': 'Offer',
                    url: variantURL,
                    availability: variantAvailability,
                    itemCondition: 'https://schema.org/NewCondition',
                    priceCurrency,
                    price: variantPrice,
                    priceSpecification: {
                        '@type': 'UnitPriceSpecification',
                        priceCurrency,
                        price: variantPrice,
                        minPrice: variantMinPrice,
                        maxPrice: variantMaxPrice,
                    },
                },
            });
        });
    }
    if (colorSet.size > 1) {
        variesBy.push('https://schema.org/color');
    }
    if (materialSet.size > 1) {
        variesBy.push('https://schema.org/material');
    }
    if (sizeSet.size > 1) {
        variesBy.push('https://schema.org/size');
    }
    if (variants.length > 0) {
        const structuredDataGroup = {
            '@context': 'https://schema.org',
            '@type': 'ProductGroup',
            productGroupID: product.sku,
            image: images,
            name: mainItemName,
            url: url.replace('http://', 'https://'),
            category,
            description,
            keywords,
            color: ((_a = product.structured_data) === null || _a === void 0 ? void 0 : _a.color) || undefined,
            material: ((_b = product.structured_data) === null || _b === void 0 ? void 0 : _b.material) || undefined,
            size: ((_c = product.structured_data) === null || _c === void 0 ? void 0 : _c.size) || undefined,
            brand: {
                '@type': 'Brand',
                name: brand,
            },
            audience: {
                '@type': 'PeopleAudience',
                suggestedGender: 'unisex',
            },
            variesBy,
            hasVariant: variants,
        };
        return structuredDataGroup;
    }
    const additionalProperties = [];
    const additionalPropertiesPairs = [
        [
            (_d = product.structured_data) === null || _d === void 0 ? void 0 : _d.additional_property_1_name,
            (_e = product.structured_data) === null || _e === void 0 ? void 0 : _e.additional_property_1_value,
        ],
        [
            (_f = product.structured_data) === null || _f === void 0 ? void 0 : _f.additional_property_2_name,
            (_g = product.structured_data) === null || _g === void 0 ? void 0 : _g.additional_property_2_value,
        ],
        [
            (_h = product.structured_data) === null || _h === void 0 ? void 0 : _h.additional_property_3_name,
            (_j = product.structured_data) === null || _j === void 0 ? void 0 : _j.additional_property_3_value,
        ],
        [
            (_k = product.structured_data) === null || _k === void 0 ? void 0 : _k.additional_property_4_name,
            (_l = product.structured_data) === null || _l === void 0 ? void 0 : _l.additional_property_4_value,
        ],
        [
            (_m = product.structured_data) === null || _m === void 0 ? void 0 : _m.additional_property_5_name,
            (_o = product.structured_data) === null || _o === void 0 ? void 0 : _o.additional_property_5_value,
        ],
    ];
    additionalPropertiesPairs.forEach(additionalPropertyPair => {
        if (additionalPropertyPair[0] && additionalPropertyPair[1]) {
            additionalProperties.push({
                '@type': 'PropertyValue',
                name: additionalPropertyPair[0],
                value: additionalPropertyPair[1],
            });
        }
    });
    const structuredDataProduct = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        sku: product.sku,
        image: images,
        name: mainItemName,
        url: url.replace('http://', 'https://'),
        category,
        description,
        keywords,
        color: ((_p = product.structured_data) === null || _p === void 0 ? void 0 : _p.color) || undefined,
        material: ((_q = product.structured_data) === null || _q === void 0 ? void 0 : _q.material) || undefined,
        size: ((_r = product.structured_data) === null || _r === void 0 ? void 0 : _r.size) || undefined,
        brand: {
            '@type': 'Brand',
            name: brand,
        },
        audience: {
            '@type': 'PeopleAudience',
            suggestedGender: 'unisex',
        },
        additionalProperty: additionalProperties.length > 0 ? additionalProperties : undefined,
        offers: {
            '@type': 'Offer',
            url: url.replace('http://', 'https://'),
            availability,
            itemCondition: 'https://schema.org/NewCondition',
            priceCurrency,
            price,
            priceSpecification: {
                '@type': 'UnitPriceSpecification',
                priceCurrency,
                minPrice,
                maxPrice,
                price,
            },
        },
    };
    return structuredDataProduct;
};
const getBreadcrumbListStructuredData = (product, locale) => {
    const domain = process.env.NEXT_PUBLIC_SITE_URL;
    const itemListElement = [
        {
            position: 1,
            name: 'Home',
            url: `${domain}/${locale}`,
        },
        ...product.categories.map((category, index) => ({
            position: index + 2,
            name: category.name,
            url: `${domain}/${locale}/c/${category.url_path}`,
        })),
        {
            position: product.categories.length + 2,
            name: product.name,
            url: null,
        },
    ];
    const breadcrumbList = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: itemListElement.map(item => ({
            '@type': 'ListItem',
            position: item.position,
            item: {
                ...(item.url && { '@id': item.url }),
                name: item.name,
            },
        })),
    };
    return breadcrumbList;
};
function extractVariantsFromProductName({ name, siteFilters = null, }) {
    if (!name)
        return null;
    let variants = '';
    const splits = (siteFilters === null || siteFilters === void 0 ? void 0 : siteFilters.splits) || [{ split: ' - ', keepIndex: 0 }];
    splits.forEach(item => {
        const splitArr = name.split(item.split);
        if (splitArr.length > 1) {
            splitArr.splice(item.keepIndex, 1);
            const joinedSplit = splitArr.join(';');
            if (variants === '') {
                variants += joinedSplit;
            }
            else {
                variants += `;${joinedSplit}`;
            }
            return joinedSplit;
        }
        return null;
    });
    return variants;
}
function formatProductName({ name, lang, siteFilters = null }) {
    var _a;
    let filteredName = name.replace(' and ', ' & ').replace('&amp;', '&');
    const splits = (siteFilters === null || siteFilters === void 0 ? void 0 : siteFilters.splits) || [{ split: ' - ', keepIndex: 0 }];
    (_a = siteFilters === null || siteFilters === void 0 ? void 0 : siteFilters.replacements) === null || _a === void 0 ? void 0 : _a.forEach(item => {
        var _a;
        if (((_a = item.langTargets) === null || _a === void 0 ? void 0 : _a.includes(lang)) || !item.langTargets) {
            filteredName = filteredName.replace(item.target, item.replacement);
        }
    });
    splits.forEach(item => {
        var _a;
        if (!item.langTargets || ((_a = item.langTargets) === null || _a === void 0 ? void 0 : _a.includes(lang))) {
            const splitArr = filteredName.split(item.split);
            filteredName = splitArr[item.keepIndex];
        }
    });
    return filteredName;
}
function handleLeadingZeros(num) {
    return num === 0 ? '' : num.toString();
}

export { extractVariantsFromProductName, filterFeaturedProducts, formatProductName, getAddonName, getBreadcrumbListStructuredData, getConfigurableOptionsText, getProductLink, getProductLinkFromUrlRewrites, getProductStructuredData, getProductUrlKeyFromPath, getProductWithMergedDynamicData, getRouteProductLink, getStockInfo, handleLeadingZeros, isBackorderAllowed, isProductAvailableForSale, isProductInStock };
//# sourceMappingURL=product.js.map
